interface IRouteData {
    routes: IRoute[]
}
interface IRouteAddress {
    line1: string;
    line2: string;
    zipCode: string;
    city: string;
    country: string;
}
interface IRoute {
    geometry: string;
    duration: number;
    service: number;
    vehicle: number;
    depth: number;
    distance: number;
    steps: IRouteStep[];
}
interface IRouteStep {
    location: number[];
    name: string;
    cost: string;
    address: IRouteAddress;
}

class SiteElementRoute extends QueryElement<HTMLElement> {
    private _map: SiteElementMap;
    private _routeData: IRouteData;
    private _colors: { bg: string; text: string }[] = [
        { bg: 'red', text: 'white' },
        { bg: 'blue', text: 'white' },
        { bg: 'green', text: 'white' },
        { bg: 'orange', text: 'black' },
        { bg: 'purple', text: 'white' },
        { bg: 'cyan', text: 'black' },
        { bg: 'magenta', text: 'white' },
        { bg: 'yellow', text: 'black' },
        { bg: 'lime', text: 'black' },
        { bg: 'pink', text: 'black' },
        { bg: 'teal', text: 'white' },
        { bg: 'brown', text: 'white' },
        { bg: 'black', text: 'white' },
        { bg: 'gray', text: 'black' },
        { bg: 'violet', text: 'black' },
        { bg: 'indigo', text: 'white' },
        { bg: 'gold', text: 'black' },
        { bg: 'silver', text: 'black' },
        { bg: 'maroon', text: 'white' },
        { bg: 'navy', text: 'white' }
    ];
    private _routeTable: IQueryElement;
    private _routePolylines: { [key: string]: any } = {};
    private _routeMarkers: { [key: string]: any[] } = {};

    constructor(card: SiteCard, element: HTMLElement) {
        super(element, false);

        // Get the route data from the card model.
        let path = this.getAttr("data-path");
        let route = path && card.Model.get(SiteModel.split(path));
        this._routeData = route;

        // Locate the map container and the table container.
        const map = this.query(".map").create<SiteElementMap, HTMLElement>(
            a => new SiteElementMap(card, a)
        );
        const routeTable = this.query(".list").first();
        if (map === null || routeTable === null) {
            throw "Not all elements found";
        }
        this._map = map;
        this._routeTable = routeTable;

        this._map.on("qts.initialized", () => {
            this.setupRoutes(this._map.getMap());
        });
    }

    /**
     * Creates Google Maps polylines and markers for each route,
     * builds a route details table, and sets up events for zooming to or toggling route visibility.
     */
    private setupRoutes(map: any): void {
        const routePolylines: { [key: string]: any } = {};
        const routeMarkers: { [key: string]: any[] } = {};
        const globalBounds = new google.maps.LatLngBounds();

        // Accumulators for totals.
        let totalVehicles = 0;
        let sumTotalDuration = 0;
        let sumRouteDuration = 0;
        let sumDistance = 0;
        let sumStops = 0;

        this._routeData.routes.forEach((route: IRoute, index: number) => {
            totalVehicles++; // Count this route (vehicle).
            const { bg, text } = this._colors[index % this._colors.length];
            const routeId = `route-${index}`;
            const markers: any[] = [];
            const bounds = new google.maps.LatLngBounds();

            // Decode the encoded polyline using Google Maps’ geometry library.
            if (route.geometry) {
                const decodedPath = google.maps.geometry.encoding.decodePath(route.geometry);
                decodedPath.forEach((latlng: any) => {
                    bounds.extend(latlng);
                    globalBounds.extend(latlng);
                });

                // Create a polyline for the route.
                const polyline = new google.maps.Polyline({
                    path: decodedPath,
                    geodesic: true,
                    strokeColor: bg,
                    strokeOpacity: 1.0,
                    strokeWeight: 4,
                    map: map
                });
                routePolylines[routeId] = polyline;
            }
            routeMarkers[routeId] = markers;

            const totalDuration = route.duration + route.service;
            sumTotalDuration += totalDuration;
            sumRouteDuration += route.duration;
            sumDistance += route.distance;
            sumStops += route.steps.length;

            // Build the route row container.
            const container = QueryElement.QueryElement("div");
            const row = QueryElement.QueryElement("dl")
                .addClass(`state`)
                .addClass(`depth-${route.depth}`);
            row.setHtml(`
                <span class="states">
                    <span class="state-item" style="background: ${bg}"></span>
                </span>
                <span class="right">
                    <i class="down">keyboard_arrow_down</i>
                    <i class="up">keyboard_arrow_up</i>
                </span>
                <dt>${route.vehicle}</dt>
                <dd>Duur: ${Math.floor(totalDuration / 3600)}h ${Math.floor(totalDuration / 60) % 60}m</dd>
                <dd>Reistijd: ${Math.floor(route.duration / 3600)}h ${Math.floor(route.duration / 60) % 60}m</dd>
                <dd>Km: ${(route.distance / 1000).toFixed(1)} / Stops: ${route.steps.length}</dd>
            `);
            container.append(row);

            // Build the detailed step rows.
            const detailRow = QueryElement.QueryElement("div").addClass("route-detail");

            route.steps.forEach((step: IRouteStep, stepIndex: number) => {
                // Note: In your original code, step.location was [lng, lat]. For Google Maps, create a LatLng accordingly.
                const [lng, lat] = step.location;
                const latLng = new google.maps.LatLng(lat, lng);
                bounds.extend(latLng);
                globalBounds.extend(latLng);

                // Create a marker. (Using a custom symbol here; you could also use marker labels.)
                const marker = new google.maps.Marker({
                    position: latLng,
                    map: map,
                    label: {
                        text: String(stepIndex + 1),
                        color: text,
                        fontWeight: 'bold'
                    },
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        fillColor: bg,
                        fillOpacity: 1,
                        strokeColor: bg,
                        strokeWeight: 1,
                        scale: 8
                    }
                });
                markers.push(marker);

                // Setup an InfoWindow for the marker.
                const popupContent = `
                    <div class="address">
                        <h1>${step.name}</h1>
                        ${step.address?.line1 ? `<div>${step.address.line1}</div>` : ''}
                        ${step.address?.line2 ? `<div>${step.address.line2}</div>` : ''}
                        ${step.address ? `<div>${step.address.zipCode} ${step.address.city}, ${step.address.country}</div>` : ''}
                    </div>
                `;
                const infowindow = new google.maps.InfoWindow({
                    closeButton: false,
                    content: popupContent
                });
                marker.addListener('mouseover', () => {
                    infowindow.open(map, marker);
                });
                marker.addListener('mouseout', () => {
                    infowindow.close();
                });

                // Create a table row for the step.
                const itemRow = QueryElement.QueryElement("dl")
                    .addClass(`depth-${route.depth + 1}`)
                    .addClass('state')
                    .setHtml(`
                    <span class="states">
                        <span class="state-item" style="background: ${bg}"></span>
                    </span>
                    <dt>${stepIndex + 1}. ${step.name}</dt>
                    <div class="address">
                        <dd>${step.address?.line1 ?? ''}</dd>
                        <dd>${step.address?.zipCode ?? ''}</dd>
                        <dd>${step.address?.city ?? ''}</dd>
                        <dd>${step.address?.country ?? ''}</dd>
                        <dd>Kosten: ${step.cost}</dd>
                    </div>
                `);
                itemRow.on("click", function (e) {
                    if (itemRow.containsClass("selected")) {
                        itemRow.removeClass("selected");
                        // Zoom out to show the whole route.
                        map.fitBounds(bounds, { padding: 50 });
                    } else {
                        itemRow.addClass("selected");
                        // Pan to the selected step and zoom in.
                        map.panTo(latLng);
                        map.setZoom(14);
                    }
                    e.stop();
                });
                detailRow.append(itemRow);
            });
            container.append(detailRow);
            this._routeTable.append(container);

            // Add a click handler for the whole route container.
            container.on('click', () => {
                if (container.containsClass('selected')) {
                    container.removeClass('selected');
                    // Reset all route polylines to default stroke.
                    Object.keys(routePolylines).forEach(otherRouteId => {
                        const polyline = routePolylines[otherRouteId];
                        if (polyline) {
                            polyline.setOptions({ strokeWeight: 4, zIndex: 0 });
                        }
                        // Reset zIndex of markers.
                        routeMarkers[otherRouteId]?.forEach(marker => {
                            marker.setZIndex(0);
                        });
                    });
                    map.fitBounds(globalBounds, { padding: 50 });
                } else {
                    container.addClass('selected');
                    map.fitBounds(bounds, { padding: 50 });
                    // Emphasize the selected route.
                    const polyline = routePolylines[routeId];
                    if (polyline) {
                        polyline.setOptions({ strokeWeight: 6, zIndex: 10 });
                    }
                    markers.forEach(marker => marker.setZIndex(10));
                    // Dim other routes.
                    Object.keys(routePolylines).forEach(otherRouteId => {
                        if (otherRouteId !== routeId) {
                            const otherPolyline = routePolylines[otherRouteId];
                            if (otherPolyline) {
                                otherPolyline.setOptions({ strokeWeight: 2, zIndex: 0 });
                            }
                            routeMarkers[otherRouteId]?.forEach(marker => marker.setZIndex(0));
                        }
                    });
                }
            });
        });

        // Append a totals row to the route table.
        const totalRow = QueryElement.QueryElement("dl").addClass("route-total");
        totalRow.setHtml(`
        <dt>Totaal</dt>
        <dd>Wagens: ${totalVehicles}</dd>
        <dd>Duur: ${Math.floor(sumTotalDuration / 3600)}h ${Math.floor(sumTotalDuration / 60) % 60}m</dd>
        <dd>Reistijd: ${Math.floor(sumRouteDuration / 3600)}h ${Math.floor(sumRouteDuration / 60) % 60}m</dd>
        <dd>Km: ${(sumDistance / 1000).toFixed(1)} /${sumStops} stops</dd>
      `);
        this._routeTable.append(totalRow);

        if (!globalBounds.isEmpty()) {
            map.fitBounds(globalBounds, { padding: 50 });
        }

        // Optionally save the polylines and markers for later use.
        this._routePolylines = routePolylines;
        this._routeMarkers = routeMarkers;
    }
}  